<template>
  <q-card-section>
    <div id="intercom-leasing-velocity" style="overflow-x: auto">
      <div
        v-if="!numberUnits"
        class="full-width flex flex-center text-center text-primary q-pa-xl"
        style="font-size: 12px; min-height: 300px"
      >
        Building unit count required for leasing velocity calculation
      </div>
      <div
        v-else-if="turnoverRate === ''"
        class="full-width flex flex-center text-center text-primary q-pa-xl"
        style="font-size: 12px; min-height: 300px"
      >
        Turnover Rate required
      </div>
      <div
        v-else-if="isLeaseUp"
        class="full-width flex flex-center text-center text-primary q-pa-xl"
        style="font-size: 12px; min-height: 300px"
      >
        Leasing velocity is not available for lease-up properties.
      </div>
      <template v-else>
        <div style="width: 100%; height: 300px">
          <loading-overlay v-if="isLoading" />
          <bar-chart v-else-if="hasChartData" :data="chartData" :options="chartOptions" />
          <slot v-else name="empty-state">
            <div
              class="full-width full-height flex flex-center text-center text-primary"
              style="font-size: 12px"
            >
              Data Required
            </div>
          </slot>
        </div>
        <q-table
          v-if="hasChartData"
          :rows="velocityRows"
          :columns="tableColumns"
          :pagination="{ rowsPerPage: 12 }"
          dense
          bordered
          flat
          class="q-mt-md"
          separator="horizontal"
          row-key="label"
          :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <template #header-cell="{ col }">
            <th :class="col.headerClass" :style="{ textAlign: col.align }">
              <span
                class="row items-center"
                :style="{ justifyContent: col.align === 'left' ? 'flex-start' : 'center' }"
              >
                {{ col.label }}
                <template v-if="col.tooltip">
                  <q-icon name="help_outline" size="xs" class="q-ml-xs text-grey cursor-pointer" />
                  <q-tooltip max-width="250px">{{ col.tooltip }}</q-tooltip>
                </template>
              </span>
            </th>
          </template>
          <template #body-cell-label="{ row }">
            <td :class="row.isHistorical ? ($q.dark.isActive ? 'text-grey-5' : 'text-grey-7') : ''">
              {{ row.label }}
            </td>
          </template>
          <template #body-cell-occupancy="{ row }">
            <td
              align="center"
              :class="row.isHistorical ? ($q.dark.isActive ? 'text-grey-5' : 'text-grey-7') : ''"
            >
              {{ formatPercent(row.occupancy) }}
            </td>
          </template>
          <template #body-cell-occupiedUnits="{ row }">
            <td
              align="center"
              :class="row.isHistorical ? ($q.dark.isActive ? 'text-grey-5' : 'text-grey-7') : ''"
            >
              {{ row.occupiedUnits }}
            </td>
          </template>
          <template #body-cell-netAbsorption="{ row }">
            <td
              align="center"
              :class="row.isHistorical ? ($q.dark.isActive ? 'text-grey-5' : 'text-grey-7') : ''"
            >
              {{ row.netAbsorption >= 0 ? '+' : '' }}{{ row.netAbsorption }}
            </td>
          </template>
          <template #body-cell-moveOuts="{ row }">
            <td
              align="center"
              :class="row.isHistorical ? ($q.dark.isActive ? 'text-grey-5' : 'text-grey-7') : ''"
            >
              {{ row.moveOuts }}
            </td>
          </template>
          <template #body-cell-leasesNeeded="{ row }">
            <td
              align="center"
              class="text-bold"
              :class="row.isHistorical ? ($q.dark.isActive ? 'text-grey-5' : 'text-grey-7') : ''"
            >
              {{ row.leasesNeeded }}
            </td>
          </template>
        </q-table>
      </template>
    </div>
  </q-card-section>
</template>

<script lang="ts">
import { type PropType, computed } from 'vue'
import type { PropertyDetailsResponse, OccupancyProjections } from 'common'
import { useSinglePropertyRentProjection } from '@/queries/rentProjections.ts'
import LoadingOverlay from '@/components/MarketAnalytics/LoadingOverlay.vue'
import { Bar as BarChart } from 'vue-chartjs'
import type { ChartData, ChartOptions } from 'chart.js'
import { unitTypeColor, formatPercent } from '@/utils'
import { format, addMonths, parseISO } from 'date-fns'

interface OccupancyPoint {
  label: string
  date: string
  occupancy: number
  isHistorical: boolean
}

interface LeasingVelocityRow {
  label: string
  isHistorical: boolean
  occupancy: number
  occupiedUnits: number
  netAbsorption: number
  moveOuts: number
  leasesNeeded: number
}

export default {
  name: 'LeasingVelocityChart',
  components: { LoadingOverlay, BarChart },
  props: {
    subjectProperty: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    frequency: {
      type: String as PropType<'yearly' | 'monthly'>,
      default: 'yearly'
    },
    turnoverRate: {
      type: [Number, String] as PropType<number | string>,
      default: 50,
      description: 'Value between 0 and 100 representing the annual turnover rate percentage'
    }
  },
  setup(props) {
    const lat = computed(() => props.subjectProperty?.lat ?? null)
    const lng = computed(() => props.subjectProperty?.lon ?? null)
    const { occupancyProjections, historicalOccupancy, isLoading } =
      useSinglePropertyRentProjection(lat, lng)
    return { occupancyProjections, historicalOccupancy, isLoading }
  },
  computed: {
    numberUnits(): number {
      return this.subjectProperty?.number_units ?? 0
    },
    isLeaseUp(): boolean {
      return this.subjectProperty?.is_lease_up ?? false
    },
    tableColumns() {
      return [
        { name: 'label', label: 'Period', field: 'label', align: 'left' as const },
        {
          name: 'occupancy',
          label: 'Projected Occupancy %',
          field: 'occupancy',
          align: 'center' as const,
          tooltip:
            "Historical periods use actual occupancy % from unit data. Forecast periods are estimated using HelloData's forecasting model for this period."
        },
        {
          name: 'occupiedUnits',
          label: 'Occupied Units',
          field: 'occupiedUnits',
          align: 'center' as const,
          tooltip: 'Total Units in the building multiplied by the projected occupancy %'
        },
        {
          name: 'netAbsorption',
          label: 'Net Absorption',
          field: 'netAbsorption',
          align: 'center' as const,
          tooltip: 'Change in occupancy period over period'
        },
        {
          name: 'moveOuts',
          label: 'Move-Outs',
          field: 'moveOuts',
          align: 'center' as const,
          tooltip:
            'Historical periods use actual move-out counts from unit data. Forecast periods estimate move-outs using the turnover rate.'
        },
        {
          name: 'leasesNeeded',
          label: 'Projected Leasing Velocity',
          field: 'leasesNeeded',
          align: 'center' as const,
          tooltip: 'Leasing velocity based on projected occupancy'
        }
      ]
    },
    velocityRows(): LeasingVelocityRow[] {
      return this.frequency === 'yearly' ? this.getYearlyRows() : this.getMonthlyRows()
    },
    hasChartData(): boolean {
      return this.velocityRows.length > 0
    },
    chartData(): ChartData<'bar'> {
      const accentColor = unitTypeColor(4, 0.7)
      const mutedColor = this.$q.dark.isActive ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'
      return {
        labels: this.velocityRows.map((r) => r.label),
        datasets: [
          {
            label: 'Projected Leasing Velocity',
            data: this.velocityRows.map((r) => r.leasesNeeded),
            backgroundColor: this.velocityRows.map((r) =>
              r.isHistorical ? mutedColor : accentColor
            ),
            borderColor: this.velocityRows.map((r) => (r.isHistorical ? mutedColor : accentColor)),
            borderWidth: 1,
            borderRadius: 3
          }
        ]
      }
    },
    chartOptions(): ChartOptions<'bar'> {
      const yLabel = this.frequency === 'yearly' ? 'Leases / Year' : 'Leases / Month'
      return {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                const val = context.parsed.y
                return `${context.dataset.label}: ${Math.round((val as number) * 10) / 10}`
              }
            }
          }
        },
        scales: {
          x: {
            grid: { drawTicks: false, drawOnChartArea: false },
            ticks: { autoSkip: true, maxRotation: 0, minRotation: 0 }
          },
          y: {
            beginAtZero: true,
            title: { display: true, text: yLabel },
            ticks: { callback: (value) => Math.round(value as number) }
          }
        }
      }
    }
  },
  methods: {
    formatPercent,
    avgHistoricalBy(frequency: 'yearly' | 'monthly'): Record<string, number> {
      if (!this.historicalOccupancy) return {}
      const isYearly = frequency === 'yearly'
      const currentYear = new Date().getFullYear()
      const grouped = this.historicalOccupancy.reduce(
        (acc, point) => {
          const key = point.date.slice(0, isYearly ? 4 : 7)
          if (isYearly) {
            const year = parseInt(key)
            if (year < currentYear - 5 || year >= currentYear) return acc
          }
          if (!acc[key]) acc[key] = { sum: 0, count: 0 }
          acc[key].sum += point.occupancy
          acc[key].count++
          return acc
        },
        {} as Record<string, { sum: number; count: number }>
      )
      const result: Record<string, number> = {}
      Object.entries(grouped).forEach(([key, data]) => {
        result[key] = data.sum / data.count
      })
      return result
    },
    actualMoveOutsBy(frequency: 'yearly' | 'monthly'): Record<string, number> {
      const keyLen = frequency === 'monthly' ? 7 : 4
      const result: Record<string, number> = {}
      this.subjectProperty?.building_availability?.forEach((unit) => {
        unit.availability_periods?.forEach((period) => {
          if (period.exit_market) {
            const key = period.exit_market.slice(0, keyLen)
            result[key] = (result[key] ?? 0) + 1
          }
        })
      })
      return result
    },
    buildVelocityRows(
      points: OccupancyPoint[],
      actualMoveOuts: Record<string, number>
    ): LeasingVelocityRow[] {
      const isMonthly = this.frequency === 'monthly'
      const rows: LeasingVelocityRow[] = []
      for (let i = 1; i < points.length; i++) {
        const prior = points[i - 1]
        const curr = points[i]
        if (!prior || !curr) {
          continue
        }
        const priorOccupied = this.numberUnits * prior.occupancy
        const currOccupied = this.numberUnits * curr.occupancy
        const netAbsorption = currOccupied - priorOccupied
        const periodKey = isMonthly ? curr.date.slice(0, 7) : curr.date.slice(0, 4)
        const actualCount = actualMoveOuts[periodKey] ?? 0
        const turnoverRateNum = Number(this.turnoverRate)
        const annualTurnover = turnoverRateNum / 100
        const effectiveTurnover = isMonthly ? annualTurnover / 12 : annualTurnover
        const moveOuts = curr.isHistorical ? actualCount : priorOccupied * effectiveTurnover
        const leasesNeeded = netAbsorption + moveOuts
        rows.push({
          label: curr.label,
          isHistorical: curr.isHistorical,
          occupancy: curr.occupancy,
          occupiedUnits: Math.round(currOccupied),
          netAbsorption: Math.round(netAbsorption * 10) / 10,
          moveOuts: Math.round(moveOuts * 10) / 10,
          leasesNeeded: Math.round(leasesNeeded * 10) / 10
        })
      }
      return rows
    },
    getYearlyRows(): LeasingVelocityRow[] {
      if (!this.occupancyProjections || !this.numberUnits) {
        return []
      }
      const { forecast_occupancy, current_occupancy } = this
        .occupancyProjections as OccupancyProjections
      const currentYear = new Date().getFullYear()

      const histPoints: OccupancyPoint[] = Object.entries(this.avgHistoricalBy('yearly'))
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([year, occ]) => ({
          label: year,
          date: `${year}-01-01`,
          occupancy: occ,
          isHistorical: true
        }))

      const currentPoint: OccupancyPoint = {
        label: String(currentYear),
        date: format(new Date(), 'yyyy-01-01'),
        occupancy: current_occupancy || 0,
        isHistorical: false
      }

      const forecastPoints: OccupancyPoint[] = Object.entries(forecast_occupancy)
        .sort(([a], [b]) => parseInt(a.replace('y', '')) - parseInt(b.replace('y', '')))
        .map(([key, occ]) => {
          const year = currentYear + parseInt(key.replace('y', ''))
          return {
            label: String(year),
            date: `${year}-01-01`,
            occupancy: occ || 0,
            isHistorical: false
          }
        })

      return this.buildVelocityRows(
        [...histPoints, currentPoint, ...forecastPoints],
        this.actualMoveOutsBy('yearly')
      )
    },
    getMonthlyRows(): LeasingVelocityRow[] {
      if (!this.occupancyProjections || !this.numberUnits) {
        return []
      }
      const { seasonal_occupancy, current_occupancy } = this
        .occupancyProjections as OccupancyProjections
      const currentDate = new Date()

      const histPoints: OccupancyPoint[] = Object.entries(this.avgHistoricalBy('monthly'))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([monthKey, occ]) => ({
          label: format(parseISO(monthKey + '-01'), 'MMM yyyy'),
          date: monthKey + '-01',
          occupancy: occ,
          isHistorical: true
        }))

      const currentPoint: OccupancyPoint = {
        label: format(currentDate, 'MMM yyyy'),
        date: format(currentDate, 'yyyy-MM-dd'),
        occupancy: current_occupancy || 0,
        isHistorical: false
      }

      const forecastPoints: OccupancyPoint[] = []
      Object.entries(seasonal_occupancy)
        .sort(([a], [b]) => parseInt(a.replace('y', '')) - parseInt(b.replace('y', '')))
        .forEach(([yearKey, monthlyData]) => {
          const yearNum = parseInt(yearKey.replace('y', ''))
          monthlyData?.forEach((occ, monthIdx) => {
            const monthDate = addMonths(currentDate, (yearNum - 1) * 12 + monthIdx + 1)
            forecastPoints.push({
              label: format(monthDate, 'MMM yyyy'),
              date: format(monthDate, 'yyyy-MM-dd'),
              occupancy: occ || 0,
              isHistorical: false
            })
          })
        })

      return this.buildVelocityRows(
        [...histPoints, currentPoint, ...forecastPoints],
        this.actualMoveOutsBy('monthly')
      )
    }
  }
}
</script>
